// 订单查询工具函数

import type { OrderData } from '@/shared/api/types';
import type { ExtendedSearchOrdersParams, ExportFormat, DateRange, AmountRange } from '../types';
import { formatDate as i18nFormatDate, formatCurrency as i18nFormatCurrency } from '@/shared/i18n/utils';

/**
 * 格式化日期为可读字符串
 */
export const formatDate = (dateString: string | Date, format: 'short' | 'long' = 'short'): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  if (format === 'short') {
    return i18nFormatDate(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } else {
    return i18nFormatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
};

/**
 * 格式化金额
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return i18nFormatCurrency(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * 获取状态颜色
 */
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    pending: '#faad14',    // 黄色
    processing: '#1890ff', // 蓝色
    shipped: '#722ed1',    // 紫色
    delivered: '#52c41a',  // 绿色
    cancelled: '#ff4d4f',  // 红色
    refunded: '#f5222d',   // 红色
    on_hold: '#d4b106',    // 橙色
    completed: '#52c41a',  // 绿色
  };

  return statusColors[status.toLowerCase()] || '#d9d9d9';
};

/**
 * 获取状态文本
 */
export const getStatusText = (status: string, t?: (key: string) => string): string => {
  const statusKeyMap: Record<string, string> = {
    pending: 'order.query.filters.statusPending',
    processing: 'order.query.filters.statusProcessing',
    shipped: 'order.query.filters.statusShipped',
    delivered: 'order.query.filters.statusDelivered',
    cancelled: 'order.query.filters.statusCancelled',
    refunded: 'order.query.filters.statusRefunded',
    on_hold: 'order.query.filters.statusOnHold',
    completed: 'order.query.filters.statusCompleted',
  };

  const statusTexts: Record<string, string> = {
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
    on_hold: 'On Hold',
    completed: 'Completed',
  };

  const lowerStatus = status.toLowerCase();

  if (t && statusKeyMap[lowerStatus]) {
    return t(statusKeyMap[lowerStatus]);
  }

  return statusTexts[lowerStatus] || status;
};

/**
 * 转换搜索参数为后端格式
 */
export const transformSearchParams = (
  params: ExtendedSearchOrdersParams
): Record<string, any> => {
  const backendParams: Record<string, any> = {
    company_id: params.company_id,
    limit: params.limit || 20,
    offset: params.offset || 0,
  };

  // 标准字段
  if (params.customer_id) backendParams.customer_id = params.customer_id;
  if (params.status) backendParams.status = params.status;
  if (params.start_date) backendParams.start_date = params.start_date;
  if (params.end_date) backendParams.end_date = params.end_date;


  return backendParams;
};

/**
 * 应用快速筛选（仅包含后端支持的筛选类型）
 */
export const applyQuickFilter = (
  filterType: string,
  baseParams: ExtendedSearchOrdersParams
): ExtendedSearchOrdersParams => {
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  switch (filterType) {
    case 'today':
      return {
        ...baseParams,
        offset: 0,
        start_date: startOfDay.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      };

    case 'pending':
      return {
        ...baseParams,
        offset: 0,
        status: 'pending'
      };

    case 'processing':
      return {
        ...baseParams,
        offset: 0,
        status: 'processing'
      };

    case 'cancelled':
      return {
        ...baseParams,
        offset: 0,
        status: 'cancelled'
      };

    case 'delivered':
      return {
        ...baseParams,
        offset: 0,
        status: 'delivered'
      };

    case 'recent':
      return {
        ...baseParams,
        offset: 0,
        start_date: sevenDaysAgo.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      };

    default:
      return baseParams;
  }
};

/**
 * 获取默认筛选预设（仅包含后端支持的筛选类型）
 */
export const getDefaultFilterPresets = (
  companyId: number,
  userRole: string,  // 新增：用户角色
  t?: (key: string) => string
) => {
  const presets = [
    {
      id: 'all_orders',
      nameKey: 'order.query.presets.allOrders.name',
      descriptionKey: 'order.query.presets.allOrders.description',
      defaultName: 'All orders',
      defaultDescription: 'View all orders',
      params: { company_id: companyId, offset: 0 },
      isDefault: true,
      visible: userRole !== 'user'  // 普通用户不可见
    },
    {
      id: 'today_orders',
      nameKey: 'order.query.presets.todayOrders.name',
      descriptionKey: 'order.query.presets.todayOrders.description',
      defaultName: "Today's orders",
      defaultDescription: 'Orders created today',
      params: {
        company_id: companyId,
        offset: 0,
        start_date: new Date().toISOString().split('T')[0]
      },
      visible: true
    },
    {
      id: 'pending_orders',
      nameKey: 'order.query.presets.pendingOrders.name',
      descriptionKey: 'order.query.presets.pendingOrders.description',
      defaultName: 'Pending orders',
      defaultDescription: 'Orders requiring processing',
      params: {
        company_id: companyId,
        offset: 0,
        status: 'pending'
      },
      visible: true
    }
  ];

  return presets
    .filter(preset => preset.visible !== false)
    .map(preset => ({
      id: preset.id,
      name: t ? t(preset.nameKey) : preset.defaultName,
      description: t ? t(preset.descriptionKey) : preset.defaultDescription,
      params: preset.params,
      isDefault: preset.isDefault
    }));
};

/**
 * 计算分页信息
 */
export const calculatePagination = (
  total: number,
  currentPage: number,
  pageSize: number
) => {
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, total);

  return {
    totalPages,
    startIndex,
    endIndex,
    hasPrevious: currentPage > 1,
    hasNext: currentPage < totalPages
  };
};

/**
 * 转换订单数据为导出格式
 */
export const convertOrdersToExportFormat = (
  orders: OrderData[],
  format: ExportFormat,
  includeAllColumns: boolean = true,
  selectedColumns: string[] = []
): string => {
  if (orders.length === 0) {
    return '';
  }

  // 确定要导出的列
  const columns = includeAllColumns
    ? Object.keys(orders[0])
    : selectedColumns.filter(col => orders[0].hasOwnProperty(col));

  switch (format) {
    case 'csv':
      return convertToCSV(orders, columns);
    case 'excel':
      // 对于Excel，我们可以生成CSV格式，或者使用更复杂的库
      return convertToCSV(orders, columns);
    case 'json':
      return JSON.stringify(orders, null, 2);
    default:
      return convertToCSV(orders, columns);
  }
};

/**
 * 转换为CSV格式
 */
const convertToCSV = (orders: OrderData[], columns: string[]): string => {
  const headers = columns.join(',');
  const rows = orders.map(order => {
    return columns.map(col => {
      const value = order[col as keyof OrderData];
      // 处理特殊值
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'object') {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }
      // 处理包含逗号或引号的字符串
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

/**
 * 生成导出文件名
 */
export const generateExportFileName = (
  format: ExportFormat,
  prefix: string = 'orders'
): string => {
  const dateStr = new Date().toISOString().split('T')[0];
  const extensions = {
    csv: 'csv',
    excel: 'xlsx',
    json: 'json'
  };

  return `${prefix}_${dateStr}.${extensions[format]}`;
};

/**
 * 下载文件
 */
export const downloadFile = (content: string, fileName: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * 防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * 生成订单状态选项
 */
export const getStatusOptions = (t?: (key: string) => string): Array<{ value: string; label: string }> => {
  const options = [
    { value: 'pending', key: 'order.query.filters.statusPending', defaultLabel: 'Pending' },
    { value: 'processing', key: 'order.query.filters.statusProcessing', defaultLabel: 'Processing' },
    { value: 'shipped', key: 'order.query.filters.statusShipped', defaultLabel: 'Shipped' },
    { value: 'delivered', key: 'order.query.filters.statusDelivered', defaultLabel: 'Delivered' },
    { value: 'cancelled', key: 'order.query.filters.statusCancelled', defaultLabel: 'Cancelled' },
    { value: 'refunded', key: 'order.query.filters.statusRefunded', defaultLabel: 'Refunded' },
    { value: 'on_hold', key: 'order.query.filters.statusOnHold', defaultLabel: 'On Hold' },
    { value: 'completed', key: 'order.query.filters.statusCompleted', defaultLabel: 'Completed' }
  ];

  return options.map(option => ({
    value: option.value,
    label: t ? t(option.key) : option.defaultLabel
  }));
};

/**
 * 生成平台选项
 */
export const getPlatformOptions = (t?: (key: string) => string): Array<{ value: string; label: string }> => {
  const options = [
    { value: 'web', key: 'order.query.platforms.web', defaultLabel: 'Website' },
    { value: 'mobile', key: 'order.query.platforms.mobile', defaultLabel: 'Mobile App' },
    { value: 'wechat', key: 'order.query.platforms.wechat', defaultLabel: 'WeChat' },
    { value: 'whatsapp', key: 'order.query.platforms.whatsapp', defaultLabel: 'WhatsApp' },
    { value: 'phone', key: 'order.query.platforms.phone', defaultLabel: 'Phone' },
    { value: 'email', key: 'order.query.platforms.email', defaultLabel: 'Email' },
    { value: 'store', key: 'order.query.platforms.store', defaultLabel: 'Store' }
  ];

  return options.map(option => ({
    value: option.value,
    label: t ? t(option.key) : option.defaultLabel
  }));
};

/**
 * 获取物流状态颜色
 */
export const getShippingStatusColor = (status: string): string => {
  const shippingStatusColors: Record<string, string> = {
    pending: '#faad14',    // 黄色：待发货
    processing: '#1890ff', // 蓝色：处理中
    shipped: '#722ed1',    // 紫色：已发货
    delivered: '#52c41a',  // 绿色：已送达
    cancelled: '#ff4d4f',  // 红色：已取消
    returned: '#f5222d',   // 红色：已退回
    in_transit: '#13c2c2', // 青色：运输中
  };
  return shippingStatusColors[status.toLowerCase()] || '#d9d9d9';
};

/**
 * 获取物流状态文本（支持i18n）
 */
export const getShippingStatusText = (status: string, t?: (key: string) => string): string => {
  const statusKeyMap: Record<string, string> = {
    pending: 'order.detail.shipping.statusPending',
    processing: 'order.detail.shipping.statusProcessing',
    shipped: 'order.detail.shipping.statusShipped',
    delivered: 'order.detail.shipping.statusDelivered',
    cancelled: 'order.detail.shipping.statusCancelled',
    returned: 'order.detail.shipping.statusReturned',
    in_transit: 'order.detail.shipping.statusInTransit',
  };

  const statusTexts: Record<string, string> = {
    pending: 'Pending',
    processing: 'Processing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    returned: 'Returned',
    in_transit: 'In Transit',
  };

  const lowerStatus = status.toLowerCase();

  if (t && statusKeyMap[lowerStatus]) {
    return t(statusKeyMap[lowerStatus]);
  }

  return statusTexts[lowerStatus] || status;
};